const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_SEGMENT = /^\d+$/;
const ULID_SEGMENT = /^[0-9a-hjkmnp-tv-z]{26}$/i;
// Booking slugs (`BK-1`), license plates (`EL12345`), nanoid tokens — mixed letters + digits.
const MIXED_LETTER_DIGIT_ID = /^(?=.*\d)(?=.*[a-z])[a-z0-9_-]{3,}$/i;
// Opaque tokens run unbroken. Allowing `-`/`_` here collapsed real route names
// instead (`stationary-summary`, `stationary-time-window`), which merged distinct
// steps into one page. Separator-carrying tokens still have a digit, so
// MIXED_LETTER_DIGIT_ID catches them.
const LONG_TOKEN = /^[a-z0-9]{16,}$/i;
const ISO_DATE_SEGMENT = /^\d{4}-\d{2}-\d{2}$/;
// Low-cardinality UI discriminators only. Filter/search values explode Pages and leak PII.
const KEPT_QUERY_KEY = /^(tab|view|lang)$/i;
// Top-level customer routes whose second segment is always a per-record slug.
// Shape-based detection cannot see a slug made only of letters (`/p/abcdefgh`),
// and one that escapes leaves a page — and, for the customer app, a Mixpanel
// event name — per recipient, which is what makes a funnel over those routes
// impossible to build.
const SLUG_OWNING_ROOT = new Set(['b', 'p', 'confirmation', 'change', 'feedback-for-booking']);

const isIdSegment = (segment: string): boolean =>
  NUMERIC_SEGMENT.test(segment) ||
  UUID_SEGMENT.test(segment) ||
  ULID_SEGMENT.test(segment) ||
  ISO_DATE_SEGMENT.test(segment) ||
  MIXED_LETTER_DIGIT_ID.test(segment) ||
  LONG_TOKEN.test(segment);

const splitPathAndSearch = (input: string): { pathname: string; search: string } => {
  try {
    if (input.startsWith('http://') || input.startsWith('https://')) {
      const url = new URL(input);
      return { pathname: url.pathname, search: url.search };
    }
  } catch {
    // Fall through and treat the input as a path.
  }

  const queryIndex = input.indexOf('?');
  if (queryIndex === -1) {
    return { pathname: input, search: '' };
  }
  return { pathname: input.slice(0, queryIndex), search: input.slice(queryIndex) };
};

const sanitizePathname = (pathname: string): string => {
  const segments = pathname.split('/');
  const sanitized = segments
    .map((segment, index) => {
      if (!segment) {
        return segment;
      }
      // segments[0] is the empty string before the leading slash, so the route
      // root is 1 and the slug it owns is 2.
      if (index === 2 && SLUG_OWNING_ROOT.has(segments[1] ?? '')) {
        return ':id';
      }
      return isIdSegment(segment) ? ':id' : segment;
    })
    .join('/');
  return sanitized || '/';
};

const sanitizeSearch = (search: string): string => {
  if (!search || search === '?') {
    return '';
  }
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  [...params.keys()].forEach((key) => {
    const value = params.get(key) ?? '';
    if (!KEPT_QUERY_KEY.test(key) || isIdSegment(value)) {
      params.delete(key);
    }
  });
  const kept = params.toString();
  return kept ? `?${kept}` : '';
};

// Collapse resource ids so OpenPanel Pages aggregates `/customers/1` and
// `/customers/2` as the same page, and so numeric/uuid/slug ids never leave the browser.
export function sanitizeAnalyticsPath(input: string): string {
  if (!input) {
    return '/';
  }
  const hashIndex = input.indexOf('#');
  const withoutHash = hashIndex === -1 ? input : input.slice(0, hashIndex);
  const { pathname, search } = splitPathAndSearch(withoutHash);
  return `${sanitizePathname(pathname)}${sanitizeSearch(search)}`;
}
